import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ContextPanel = ({ organizationProfile, decisionHistory, bestPracticeInfo, isCollapsed, onToggle }) => {
  const [activeTab, setActiveTab] = useState('organization');

  const tabs = [
    { id: 'organization', label: 'Organization', icon: 'Building2' },
    { id: 'decisions', label: 'Decisions', icon: 'GitBranch' },
    { id: 'practice', label: 'Best Practice', icon: 'CheckCircle2' }
  ];

  if (isCollapsed) {
    return (
      <div className="w-12 bg-card border-r border-border flex flex-col items-center py-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggle}
          className="mb-4"
        >
          <Icon name="ChevronRight" size={20} />
        </Button>
        {tabs?.map((tab) => (
          <Button
            key={tab?.id}
            variant="ghost"
            size="icon"
            onClick={() => {
              setActiveTab(tab?.id);
              onToggle();
            }}
            className="mb-2"
          >
            <Icon name={tab?.icon} size={16} />
          </Button>
        ))}
      </div>
    );
  }

  return (
    <div className="w-80 bg-card border-r border-border flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h2 className="text-lg font-semibold text-foreground">Context</h2>
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggle}
        >
          <Icon name="ChevronLeft" size={20} />
        </Button>
      </div>
      {/* Tabs */}
      <div className="flex border-b border-border">
        {tabs?.map((tab) => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`flex-1 flex items-center justify-center space-x-2 px-3 py-2 text-sm font-medium nav-transition ${
              activeTab === tab?.id
                ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name={tab?.icon} size={16} />
            <span className="hidden sm:inline">{tab?.label}</span>
          </button>
        ))}
      </div>
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'organization' && (
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-2">
                Organization Profile
              </h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Icon name="Building2" size={16} className="text-muted-foreground" />
                  <span className="text-sm text-foreground">{organizationProfile?.name}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Globe" size={16} className="text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">{organizationProfile?.industry}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Users" size={16} className="text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">{organizationProfile?.size}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="MapPin" size={16} className="text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">{organizationProfile?.region}</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-foreground mb-2">Current Implementation</h4>
              <div className="space-y-2">
                {organizationProfile?.currentSystems?.map((system, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-accent rounded-full"></div>
                    <span className="text-sm text-muted-foreground">{system}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'decisions' && (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-foreground mb-2">
              Decision History
            </h3>
            <div className="space-y-3">
              {decisionHistory?.map((decision) => (
                <div key={decision?.id} className="p-3 bg-muted rounded-md">
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-sm font-medium text-foreground">
                      Q{decision?.questionId}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {decision?.timestamp}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">
                    {decision?.question}
                  </p>
                  <div className="flex items-center space-x-2">
                    <Icon name="ArrowRight" size={12} className="text-accent" />
                    <span className="text-sm text-foreground font-medium">
                      {decision?.response}
                    </span>
                  </div>
                  {decision?.impact && (
                    <div className="mt-2 p-2 bg-background rounded border-l-2 border-accent">
                      <p className="text-xs text-muted-foreground">
                        Impact: {decision?.impact}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'practice' && (
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-2">
                Best Practice Information
              </h3>
              <div className="p-3 bg-muted rounded-md">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="Package" size={16} className="text-primary" />
                  <span className="text-sm font-medium text-foreground">
                    {bestPracticeInfo?.code}
                  </span>
                </div>
                <h4 className="text-sm font-semibold text-foreground mb-1">
                  {bestPracticeInfo?.title}
                </h4>
                <p className="text-xs text-muted-foreground mb-3">
                  {bestPracticeInfo?.description}
                </p>
                <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                  <span>Version: {bestPracticeInfo?.version}</span>
                  <span>LoB: {bestPracticeInfo?.lineOfBusiness}</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-foreground mb-2">Key Processes</h4>
              <div className="space-y-2">
                {bestPracticeInfo?.processes?.map((process, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <Icon name="Settings" size={12} className="text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">{process}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-foreground mb-2">Dependencies</h4>
              <div className="space-y-2">
                {bestPracticeInfo?.dependencies?.map((dep, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <Icon name="Link" size={12} className="text-accent" />
                    <span className="text-sm text-muted-foreground">{dep}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContextPanel;